import { trackEvent } from './analytics';

const LEGACY_QUEUE_STORE_KEY = 'loopino_offline_queue';
const LEGACY_SYNC_STATUS_KEY = 'loopino_sync_status';
const OFFLINE_RUNTIME_STATE_KEY = 'loopino_offline_runtime_state';
const OFFLINE_RUNTIME_VERSION = 2;

const LEGACY_INDEXED_DB_PATTERNS = [
  /loopino[-_ ]?offline/i,
  /offline[-_ ]?queue/i,
  /workbox-background-sync/i,
];

const FALLBACK_INDEXED_DB_NAMES = ['workbox-background-sync'];

export const OFFLINE_QUEUE_STATUS = 'disabled';
export const OFFLINE_QUEUE_EVENTS = {
  UPDATE: 'offlineQueueUpdate',
  INVALIDATED: 'offlineQueueInvalidated',
  BLOCKED: 'offlineQueueBlocked',
};

export const ACTION_TYPES = {
  POST_UPDATE: 'post_update',
  POST_APPROVE: 'post_approve',
  POST_REJECT: 'post_reject',
  POST_DELETE: 'post_delete',
  IMAGE_UPLOAD: 'image_upload',
  CAMPAIGN_CREATE: 'campaign_create',
  CAMPAIGN_UPDATE: 'campaign_update',
};

const isBrowser = () => typeof window !== 'undefined';

const safeParse = (value, fallback = null) => {
  if (!value || typeof value !== 'string') {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const buildDefaultRuntimeState = () => ({
  version: OFFLINE_RUNTIME_VERSION,
  status: OFFLINE_QUEUE_STATUS,
  mutationsSupported: false,
  initializedAt: Date.now(),
  lastInvalidation: null,
  lastBlockedAction: null,
  lastRecovery: null,
});

const resolveIndexedDbNames = async () => {
  if (!isBrowser() || typeof window.indexedDB?.deleteDatabase !== 'function') {
    return [];
  }

  if (typeof window.indexedDB.databases === 'function') {
    try {
      const databases = await window.indexedDB.databases();
      return databases
        .map((entry) => entry?.name)
        .filter((name) => typeof name === 'string' && LEGACY_INDEXED_DB_PATTERNS.some((pattern) => pattern.test(name)));
    } catch (error) {
      return [...FALLBACK_INDEXED_DB_NAMES];
    }
  }

  return [...FALLBACK_INDEXED_DB_NAMES];
};

const deleteIndexedDb = (name) => new Promise((resolve) => {
  if (!isBrowser() || typeof window.indexedDB?.deleteDatabase !== 'function') {
    resolve(false);
    return;
  }

  try {
    const request = window.indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve(true);
    request.onerror = () => resolve(false);
    request.onblocked = () => resolve(false);
  } catch (error) {
    resolve(false);
  }
});

class OfflineQueue {
  constructor() {
    this.queue = [];
    this.listeners = [];
    this.isSyncing = false;
    this.networkListenersInstalled = false;
    this.offlineSince = isBrowser() && navigator.onLine === false ? Date.now() : null;

    const { runtimeState, versionMismatch } = this.loadRuntimeState();
    this.runtimeState = runtimeState;
    this.versionMismatch = versionMismatch;
    this.initializationPromise = Promise.resolve(this.getStatus());

    if (isBrowser()) {
      this.installNetworkListeners();
      this.initializationPromise = this.initializeRuntime();
    }
  }

  loadRuntimeState() {
    if (!isBrowser()) {
      return {
        runtimeState: buildDefaultRuntimeState(),
        versionMismatch: false,
      };
    }

    const stored = safeParse(localStorage.getItem(OFFLINE_RUNTIME_STATE_KEY));

    if (stored?.version === OFFLINE_RUNTIME_VERSION) {
      return {
        runtimeState: {
          ...buildDefaultRuntimeState(),
          ...stored,
          version: OFFLINE_RUNTIME_VERSION,
          status: OFFLINE_QUEUE_STATUS,
          mutationsSupported: false,
        },
        versionMismatch: false,
      };
    }

    return {
      runtimeState: buildDefaultRuntimeState(),
      versionMismatch: Boolean(stored),
    };
  }

  persistRuntimeState() {
    if (!isBrowser()) {
      return;
    }

    localStorage.setItem(OFFLINE_RUNTIME_STATE_KEY, JSON.stringify(this.runtimeState));
  }

  getStatus() {
    return {
      ...this.runtimeState,
      queueSize: 0,
      pendingCount: 0,
      isSyncing: false,
    };
  }

  waitUntilReady() {
    return this.initializationPromise;
  }

  installNetworkListeners() {
    if (!isBrowser() || this.networkListenersInstalled) {
      return;
    }

    const handleOffline = () => {
      if (!this.offlineSince) {
        this.offlineSince = Date.now();
      }

      this.dispatchStatusUpdate('connection_offline');
      this.notifyListeners('connection_offline', this.getStatus());
    };

    const handleOnline = () => {
      if (!this.offlineSince) {
        this.dispatchStatusUpdate('connection_online');
        this.notifyListeners('connection_online', this.getStatus());
        return;
      }

      const recovery = {
        recoveredAt: Date.now(),
        durationMs: Math.max(Date.now() - this.offlineSince, 0),
      };

      this.offlineSince = null;
      this.runtimeState.lastRecovery = recovery;
      this.persistRuntimeState();

      trackEvent('offline_runtime_recovered', {
        duration_ms: recovery.durationMs,
        offline_mutations_supported: false,
        legacy_queue_dropped_count: this.runtimeState.lastInvalidation?.droppedCount || 0,
      });

      this.dispatchStatusUpdate('recovered', recovery);
      this.notifyListeners('recovered', recovery);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    this.networkListenersInstalled = true;
  }

  dispatchWindowEvent(name, detail) {
    if (!isBrowser()) {
      return;
    }

    window.dispatchEvent(new CustomEvent(name, { detail }));
  }

  dispatchStatusUpdate(event, data = null) {
    this.dispatchWindowEvent(OFFLINE_QUEUE_EVENTS.UPDATE, {
      event,
      ...this.getStatus(),
      data,
    });
  }

  async initializeRuntime() {
    const invalidation = await this.invalidateLegacyState();

    if (invalidation) {
      this.runtimeState.lastInvalidation = invalidation;
      this.persistRuntimeState();

      trackEvent('offline_legacy_queue_dropped', {
        dropped_count: invalidation.droppedCount,
        invalidation_reason: invalidation.reason,
        deleted_indexeddb_count: invalidation.deletedDatabases.length,
        offline_mutations_supported: false,
      });

      this.dispatchWindowEvent(OFFLINE_QUEUE_EVENTS.INVALIDATED, {
        ...this.getStatus(),
        invalidation,
      });
      this.notifyListeners('invalidated', invalidation);
    }

    this.persistRuntimeState();
    this.dispatchStatusUpdate('initialized');
    return this.getStatus();
  }

  async invalidateLegacyState() {
    if (!isBrowser()) {
      return null;
    }

    const storedQueueRaw = localStorage.getItem(LEGACY_QUEUE_STORE_KEY);
    const storedQueue = safeParse(storedQueueRaw, []);
    const hadLegacyQueue = storedQueueRaw !== null;
    const hadLegacySyncStatus = localStorage.getItem(LEGACY_SYNC_STATUS_KEY) !== null;
    const droppedCount = Array.isArray(storedQueue)
      ? storedQueue.length
      : hadLegacyQueue
        ? 1
        : 0;

    const candidateDbNames = await resolveIndexedDbNames();
    const deletedDatabases = [];

    for (const name of candidateDbNames) {
      const deleted = await deleteIndexedDb(name);
      if (deleted) {
        deletedDatabases.push(name);
      }
    }

    const requiresInvalidation =
      this.versionMismatch || hadLegacyQueue || hadLegacySyncStatus || deletedDatabases.length > 0;

    if (!requiresInvalidation) {
      return null;
    }

    localStorage.removeItem(LEGACY_QUEUE_STORE_KEY);
    localStorage.removeItem(LEGACY_SYNC_STATUS_KEY);

    return {
      invalidatedAt: Date.now(),
      droppedCount,
      deletedDatabases,
      reason: this.versionMismatch
        ? 'storage_version_mismatch'
        : hadLegacyQueue || hadLegacySyncStatus
          ? 'legacy_queue_detected'
          : 'legacy_indexeddb_detected',
    };
  }

  add(type, data) {
    const blockedAction = {
      id: `blocked_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type: type || 'unknown',
      timestamp: Date.now(),
      data: data || null,
      online: navigator.onLine === true,
      reason: navigator.onLine === true ? 'queue_disabled' : 'offline_mutations_unsupported',
    };

    this.runtimeState.lastBlockedAction = blockedAction;
    this.persistRuntimeState();

    trackEvent('offline_mutation_blocked', {
      action_type: blockedAction.type,
      blocked_reason: blockedAction.reason,
      online: blockedAction.online,
      offline_mutations_supported: false,
    });

    this.dispatchWindowEvent(OFFLINE_QUEUE_EVENTS.BLOCKED, {
      ...this.getStatus(),
      blockedAction,
    });
    this.dispatchStatusUpdate('blocked', blockedAction);
    this.notifyListeners('blocked', blockedAction);

    return null;
  }

  remove() {
    return false;
  }

  updateStatus() {
    return false;
  }

  getAll() {
    return [];
  }

  getPending() {
    return [];
  }

  size() {
    return 0;
  }

  clear() {
    this.dispatchStatusUpdate('cleared');
    this.notifyListeners('cleared', this.getStatus());
  }

  clearSynced() {}

  async syncQueue() {
    const result = {
      success: false,
      synced: 0,
      failed: 0,
      disabled: true,
      message: 'Offline mutations are disabled. Retry the action when the connection is available.',
    };

    this.dispatchStatusUpdate('sync_disabled', result);
    this.notifyListeners('sync_disabled', result);
    return result;
  }

  subscribe(callback) {
    this.listeners.push(callback);

    return () => {
      const index = this.listeners.indexOf(callback);
      if (index >= 0) {
        this.listeners.splice(index, 1);
      }
    };
  }

  notifyListeners(event, data) {
    this.listeners.forEach((callback) => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('[OfflineQueue] Listener error:', error);
      }
    });
  }
}

const offlineQueue = new OfflineQueue();

export { offlineQueue };

export default offlineQueue;
